import { useEffect, useMemo, useState } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { residentsAPI, selfServiceAPI } from '../../services/api'
import { ResidentProfileView } from '../../components/residents/modals/ViewResidentModal'
import PhotoCaptureModal from '../../components/residents/modals/PhotoCaptureModal'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { IdentificationIcon } from '@heroicons/react/24/outline'
import { useQuery } from 'react-query'

function ResidentProfilePage() {
  const user = useAuthStore((state) => state.user)

  const [resident, setResident] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [showEditModal, setShowEditModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPhotoCaptureModal, setShowPhotoCaptureModal] = useState(false)
  const [capturedPhoto, setCapturedPhoto] = useState(null)
  const [photoFile, setPhotoFile] = useState(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm()

  const residentId = useMemo(() => {
    const fromStorage = localStorage.getItem('resident_id')
    return fromStorage || user?.resident_id || user?.id || null
  }, [user?.id, user?.resident_id])

  const transformResident = (r) => {
    if (!r) return r

    return {
      ...r,
      date_of_birth: r.birthdate ?? r.date_of_birth,
      phone_number: r.contact_number ?? r.phone_number,
      house_number: r.address?.house_number || r.house_number || '',
      street: r.address?.street || r.street || '',
      purok_zone: r.address?.purok_zone || r.purok_zone || '',
      barangay: r.address?.barangay || r.barangay || '',
      barangay_code: r.address?.barangay_code || r.barangay_code || '',
      municipality: r.address?.municipality || r.municipality || '',
      municipality_code:
        r.address?.municipality_code || r.municipality_code || '',
      province: r.address?.province || r.province || '',
      province_code: r.address?.province_code || r.province_code || '',
      region: r.address?.region || r.region || '',
      region_code: r.address?.region_code || r.region_code || '',
      zip_code: r.address?.zip_code || r.zip_code || '',
      address:
        typeof r.full_address === 'string'
          ? r.full_address
          : [
              r.address?.house_number,
              r.address?.street,
              r.address?.purok_zone,
              r.address?.barangay,
              r.address?.municipality,
              r.address?.province,
              r.address?.region,
            ]
              .filter(Boolean)
              .join(', ') ||
            r.address ||
            'No address provided',
      photo_url: r.photo_url || r.profile_photo,
      profile_photo: r.profile_photo,
      qr_code: r.barangay_id_number,
      precinct_number: r.voter?.precinct_name,
      relationship_to_head: (() => {
        if (r.is_household_head) return 'Head'
        if (r.relationship_to_head && r.relationship_to_head !== 'Member') {
          return r.relationship_to_head
        }
        return 'Member'
      })(),
      emergency_contact: `${r.emergency_contact_name} - ${r.emergency_contact_number}`,
      household_members: Array.isArray(r.household_members)
        ? r.household_members
        : [],
      ayuda_programs: Array.isArray(r.ayuda_programs) ? r.ayuda_programs : [],
    }
  }

  const openEditModal = () => {
    if (!resident) return
    setCapturedPhoto(resident.photo_url || resident.profile_photo || null)
    setPhotoFile(null)

    reset({
      first_name: resident.first_name || '',
      middle_name: resident.middle_name || '',
      last_name: resident.last_name || '',
      suffix: resident.suffix || '',
      date_of_birth:
        resident.birthdate ||
        resident.date_of_birth ||
        resident.date_of_birth ||
        '',
      birthplace: resident.birthplace || '',
      gender: resident.gender || '',
      civil_status: resident.civil_status || '',
      citizenship: resident.citizenship || 'Filipino',
      religion: resident.religion || '',
      occupation: resident.occupation || '',
      monthly_income: resident.monthly_income || '',
      education_level: resident.education_level || '',

      phone_number: resident.contact_number || resident.phone_number || '',
      email: resident.email || '',
      emergency_contact_name: resident.emergency_contact_name || '',
      emergency_contact_number: resident.emergency_contact_number || '',

      house_number: resident.house_number || '',
      street: resident.street || '',
      purok_zone: resident.purok_zone || '',
      region_code: resident.region_code || '',
      province_code: resident.province_code || '',
      municipality_code: resident.municipality_code || '',
      barangay_code: resident.barangay_code || '',
      zip_code: resident.zip_code || '',

      length_of_residency: resident.length_of_residency || '',
      voter_status: resident.voter_status || '',
      precinct_number: resident.precinct_number || '',
      solo_parent_status: !!resident.solo_parent_status,
      pwd_status: !!resident.pwd_status,
      senior_citizen_status: !!resident.senior_citizen_status,
      illiterate_status: !!resident.illiterate_status,
      four_ps_number: resident.four_ps_number || '',
      indigenous_group: resident.indigenous_group || '',

      status: resident.status || 'active',
      relationship_to_head:
        resident.relationship_to_head ||
        (resident.is_household_head ? 'Head' : 'Member'),
    })

    setShowEditModal(true)
  }

  const closeEditModal = () => {
    setShowEditModal(false)
    setShowPhotoCaptureModal(false)
    setPhotoFile(null)
  }

  const handlePhotoCaptured = (file, dataUrl) => {
    setPhotoFile(file)
    setCapturedPhoto(dataUrl)
    setShowPhotoCaptureModal(false)
  }

  const refreshResident = async () => {
    if (!residentId) return
    const res = await residentsAPI.getById(residentId)
    const data = transformResident(res?.data?.data)
    setResident(data)
  }

  const onSubmitEdit = async (data) => {
    if (!residentId) return
    try {
      setIsSubmitting(true)

      const payload = {
        first_name: data.first_name,
        middle_name: data.middle_name || null,
        last_name: data.last_name,
        suffix: data.suffix || null,
        birthdate: data.date_of_birth,
        birthplace: data.birthplace,
        gender: data.gender,
        civil_status: data.civil_status || 'single',
        citizenship: data.citizenship,
        religion: data.religion || null,
        occupation: data.occupation || null,
        monthly_income: data.monthly_income
          ? parseFloat(data.monthly_income)
          : null,
        education_level: data.education_level || null,

        contact_number: data.phone_number || null,
        email: data.email || null,
        emergency_contact_name: data.emergency_contact_name || null,
        emergency_contact_number: data.emergency_contact_number || null,

        house_number: data.house_number || null,
        street: data.street || null,
        purok_zone: data.purok_zone || null,
        region_code: data.region_code || null,
        province_code: data.province_code || null,
        municipality_code: data.municipality_code || null,
        barangay_code: data.barangay_code || null,
        zip_code: data.zip_code || null,

        length_of_residency: data.length_of_residency || null,
        voter_status: data.voter_status || null,
        precinct_number: data.precinct_number || null,
        solo_parent_status: !!data.solo_parent_status,
        pwd_status: !!data.pwd_status,
        senior_citizen_status: !!data.senior_citizen_status,
        illiterate_status: !!data.illiterate_status,
        four_ps_number: data.four_ps_number || null,
        indigenous_group: data.indigenous_group || null,

        status: data.status || 'active',
        is_household_head: data.relationship_to_head === 'Head',
        relationship_to_head: data.relationship_to_head || null,
      }

      if (photoFile) {
        const photoFormData = new FormData()
        photoFormData.append('photo', photoFile)
        await residentsAPI.uploadPhoto(residentId, photoFormData)
      }

      await residentsAPI.update(residentId, payload)

      await refreshResident()
      setShowEditModal(false)
      toast.success('Profile updated successfully')
    } catch (e) {
      const message =
        e?.response?.data?.message ||
        e?.message ||
        'Failed to update profile. Please try again.'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  // useEffect(() => {
  //   let cancelled = false

  //   const load = async () => {
  //     try {
  //       setLoading(true)
  //       setError(null)
  //       setResident(null)
  //       if (!residentId) {
  //         throw new Error('No resident_id found in localStorage')
  //       }
  //       const res = await residentsAPI.getById(residentId)
  //       const data = transformResident(res?.data?.data)
  //       if (cancelled) return
  //       setResident(data)
  //     } catch (e) {
  //       if (cancelled) return
  //       setError(e?.message || 'Failed to load profile')
  //     } finally {
  //       if (cancelled) return
  //       setLoading(false)
  //     }
  //   }

  //   load()
  //   return () => {
  //     cancelled = true
  //   }
  // }, [residentId])
  const {
    data: profile,
    isLoading,
    isError,
    // error,
    refetch,
  } = useQuery({
    queryKey: ['self-profile'],
    queryFn: selfServiceAPI.getProfile,
  })
  const residentProfile = profile?.data?.data
  const residentProfileBio = profile?.data?.data?.resident

  console.log('prof', residentProfile)
  return (
    <div className='w-auto'>
      <div className='flex items-start gap-3'>
        <div className='h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center'>
          <IdentificationIcon className='h-5 w-5 text-blue-700' />
        </div>
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>
            Resident Profile
          </h2>
          <p className='text-sm text-gray-500'>
            Access resident information and digital identification details.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className='mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
          <div className='text-sm text-gray-600'>Loading...</div>
        </div>
      ) : isError ? (
        <div className='mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
          <div className='text-sm text-red-600'>{error}</div>
        </div>
      ) : (
        <div className='mt-6'>
          <ResidentProfileView
            resident={residentProfileBio}
            isModal={false}
            onEdit={openEditModal}
            hideAyudaTransactions={user?.role === 'resident'}
            hideProfileActions={user?.role === 'resident'}
          />
        </div>
      )}

      {showEditModal ? (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative mx-auto p-6 border w-full max-w-6xl shadow-2xl rounded-lg bg-white my-10'>
            <div className='flex justify-between items-center mb-6'>
              <h3 className='text-xl font-medium text-gray-900'>
                Edit Resident Profile
              </h3>
              <button
                type='button'
                onClick={closeEditModal}
                className='text-gray-400 hover:text-gray-600 text-2xl'
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmitEdit)} className='space-y-8'>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
                <div>
                  <h4 className='text-lg font-medium text-gray-900 mb-2'>
                    Profile Photo
                  </h4>
                  <div className='flex items-center space-x-4'>
                    <div className='w-44 h-44 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden'>
                      {capturedPhoto ? (
                        <img
                          src={capturedPhoto}
                          alt='Profile photo'
                          className='w-full h-full object-cover'
                          onError={() => setCapturedPhoto(null)}
                        />
                      ) : (
                        <div className='text-sm text-gray-500'>
                          No photo available
                        </div>
                      )}
                    </div>
                    <div className='space-y-2'>
                      <button
                        type='button'
                        onClick={() => setShowPhotoCaptureModal(true)}
                        className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'
                      >
                        Capture Photo
                      </button>
                      <div className='text-xs text-gray-500'>
                        Recommended: 2×2 ID photo format
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className='text-lg font-medium text-gray-900 mb-2'>
                    Household Information
                  </h4>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Relationship to Head
                  </label>
                  <select
                    {...register('relationship_to_head')}
                    className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900'
                  >
                    <option value=''>Select relationship</option>
                    <option value='Head'>Head of Household</option>
                    <option value='Spouse'>Spouse</option>
                    <option value='Son'>Son</option>
                    <option value='Daughter'>Daughter</option>
                    <option value='Father'>Father</option>
                    <option value='Mother'>Mother</option>
                    <option value='Brother'>Brother</option>
                    <option value='Sister'>Sister</option>
                    <option value='Grandfather'>Grandfather</option>
                    <option value='Grandmother'>Grandmother</option>
                    <option value='Member'>Family Member</option>
                    <option value='Other'>Other</option>
                  </select>
                </div>

                <div>
                  <h4 className='text-lg font-medium text-gray-900 mb-2'>
                    Digital ID Information
                  </h4>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Status
                  </label>
                  <select
                    {...register('status')}
                    className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900'
                  >
                    <option value='active'>Active</option>
                    <option value='inactive'>Inactive</option>
                    <option value='verified'>Verified</option>
                    <option value='pending'>Pending</option>
                  </select>
                </div>
              </div>

              <div>
                <h4 className='text-lg font-medium text-gray-900 mb-2'>
                  Personal Information
                </h4>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      First Name *
                    </label>
                    <input
                      type='text'
                      {...register('first_name', {
                        required: 'First name is required',
                      })}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                    {errors.first_name ? (
                      <p className='text-red-500 text-xs mt-2'>
                        {errors.first_name.message}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Last Name *
                    </label>
                    <input
                      type='text'
                      {...register('last_name', {
                        required: 'Last name is required',
                      })}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                    {errors.last_name ? (
                      <p className='text-red-500 text-xs mt-2'>
                        {errors.last_name.message}
                      </p>
                    ) : null}
                  </div>

                  <div className='grid grid-cols-[70%_30%] gap-3'>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>
                        Middle Name
                      </label>
                      <input
                        type='text'
                        {...register('middle_name')}
                        className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>
                        Suffix
                      </label>
                      <input
                        type='text'
                        {...register('suffix')}
                        className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                      />
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Date of Birth *
                    </label>
                    <input
                      type='date'
                      {...register('date_of_birth', {
                        required: 'Date of birth is required',
                      })}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                    {errors.date_of_birth ? (
                      <p className='text-red-500 text-xs mt-2'>
                        {errors.date_of_birth.message}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Birthplace *
                    </label>
                    <input
                      type='text'
                      {...register('birthplace', {
                        required: 'Birthplace is required',
                      })}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                    {errors.birthplace ? (
                      <p className='text-red-500 text-xs mt-2'>
                        {errors.birthplace.message}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Citizenship *
                    </label>
                    <input
                      type='text'
                      {...register('citizenship', {
                        required: 'Citizenship is required',
                      })}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                    {errors.citizenship ? (
                      <p className='text-red-500 text-xs mt-2'>
                        {errors.citizenship.message}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Gender *
                    </label>
                    <select
                      {...register('gender', {
                        required: 'Gender is required',
                      })}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    >
                      <option value=''>Select gender</option>
                      <option value='male'>Male</option>
                      <option value='female'>Female</option>
                      <option value='other'>Other</option>
                    </select>
                    {errors.gender ? (
                      <p className='text-red-500 text-xs mt-2'>
                        {errors.gender.message}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Civil Status *
                    </label>
                    <select
                      {...register('civil_status', {
                        required: 'Civil status is required',
                      })}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    >
                      <option value=''>Select status</option>
                      <option value='single'>Single</option>
                      <option value='married'>Married</option>
                      <option value='divorced'>Divorced</option>
                      <option value='widowed'>Widowed</option>
                      <option value='separated'>Separated</option>
                    </select>
                    {errors.civil_status ? (
                      <p className='text-red-500 text-xs mt-2'>
                        {errors.civil_status.message}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Religion
                    </label>
                    <input
                      type='text'
                      {...register('religion')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Occupation
                    </label>
                    <input
                      type='text'
                      {...register('occupation')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Monthly Income
                    </label>
                    <input
                      type='number'
                      {...register('monthly_income')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                      min='0'
                      step='0.01'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Education Level
                    </label>
                    <select
                      {...register('education_level')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    >
                      <option value=''>Not specified</option>
                      <option value='no_formal_education'>
                        No Formal Education
                      </option>
                      <option value='elementary'>Elementary</option>
                      <option value='high_school'>High School</option>
                      <option value='vocational'>Vocational</option>
                      <option value='college'>College</option>
                      <option value='graduate'>Graduate</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h4 className='text-lg font-medium text-gray-900 mb-2'>
                  Contact Information
                </h4>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Phone Number
                    </label>
                    <input
                      type='tel'
                      {...register('phone_number')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Email Address
                    </label>
                    <input
                      type='email'
                      {...register('email')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Emergency Contact Name
                    </label>
                    <input
                      type='text'
                      {...register('emergency_contact_name')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Emergency Contact Number
                    </label>
                    <input
                      type='tel'
                      {...register('emergency_contact_number')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      House Number
                    </label>
                    <input
                      type='text'
                      {...register('house_number')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Street
                    </label>
                    <input
                      type='text'
                      {...register('street')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Purok/Zone
                    </label>
                    <input
                      type='text'
                      {...register('purok_zone')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Region Code
                    </label>
                    <input
                      type='text'
                      {...register('region_code')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Province Code
                    </label>
                    <input
                      type='text'
                      {...register('province_code')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Municipality Code
                    </label>
                    <input
                      type='text'
                      {...register('municipality_code')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Barangay Code
                    </label>
                    <input
                      type='text'
                      {...register('barangay_code')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      ZIP Code
                    </label>
                    <input
                      type='text'
                      {...register('zip_code')}
                      className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3'
                    />
                  </div>
                </div>
              </div>

              <div className='flex justify-end gap-4 pt-6 border-t border-gray-200'>
                <button
                  type='button'
                  onClick={closeEditModal}
                  className='px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={isSubmitting}
                  className='px-6 py-3 border-2 border-transparent rounded-lg text-white font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isSubmitting ? 'Saving...' : 'Update Resident'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <PhotoCaptureModal
        show={showPhotoCaptureModal}
        onClose={() => setShowPhotoCaptureModal(false)}
        onCapture={handlePhotoCaptured}
        capturedPhoto={capturedPhoto}
        setCapturedPhoto={setCapturedPhoto}
      />
    </div>
  )
}

export default ResidentProfilePage
